export function decodeHtmlEntities(value: string | null | undefined): string {
  if (!value) return '';
  const doc = new DOMParser().parseFromString(String(value), 'text/html');
  return doc.documentElement.textContent ?? String(value);
}

export function stripHtml(value: string | null | undefined): string {
  if (!value) return '';
  const doc = new DOMParser().parseFromString(String(value), 'text/html');
  return (doc.body.textContent || '').trim();
}

const ALLOWED_TAGS = new Set([
  // Block structure
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'figure', 'figcaption',
  // Tables
  'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  // Inline formatting
  'em', 'i', 'strong', 'b', 'u', 's', 'del', 'ins',
  'mark', 'sub', 'sup', 'cite', 'abbr', 'span',
  // Media
  'img',
  // Links
  'a',
]);

// Void elements — no closing tag
const VOID_TAGS = new Set(['br', 'hr', 'img']);

function _escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function _escapeAttr(value: string): string {
  return _escapeText(value)
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function _httpUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') return url;
  } catch {
    return null;
  }
  return null;
}

/**
 * An href safe to hand to the browser, or undefined when the value isn't one.
 *
 * Every URL Kingfisher renders is third-party: it arrives from a feed, a page
 * we fetched, or a link a curator pasted. `sanitizeHtml` already restricts the
 * anchors *inside* article bodies to http(s), but the URLs a row renders in its
 * own chrome (story.url, linked_url, feed_url, canonical_url) went into `href`
 * unchecked, where a `javascript:` value is a click away from script running
 * with the session's privileges. Same allowlist as the sanitizer, one owner —
 * an `href` that takes an ingested URL should route through here.
 *
 * Returns undefined rather than '' or '#': React drops the attribute entirely,
 * so the anchor renders as inert text instead of a link to the current page.
 */
export function safeHref(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  return _httpUrl(String(value))?.href;
}

function _processNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return _escapeText(node.textContent || '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const children = Array.from(el.childNodes).map(_processNode).join('');
  if (!ALLOWED_TAGS.has(tag)) return children;

  let attrs = '';

  if (tag === 'a') {
    const href = el.getAttribute('href') || '';
    const url = _httpUrl(href);
    if (url) {
      attrs = ` href="${_escapeAttr(url.href)}" target="_blank" rel="noreferrer noopener"`;
    }
  } else if (tag === 'img') {
    const src = el.getAttribute('src') || '';
    const url = _httpUrl(src);
    if (url) {
      if (url.protocol === 'http:') url.protocol = 'https:';
      const alt = _escapeAttr(el.getAttribute('alt') || '');
      attrs = ` src="${_escapeAttr(url.href)}" alt="${alt}" loading="lazy"`;
    } else {
      // Drop images with non-http src (data URIs, relative paths, etc.)
      return '';
    }
  } else if (tag === 'abbr') {
    const title = el.getAttribute('title');
    if (title) attrs = ` title="${_escapeAttr(title)}"`;
  }

  if (VOID_TAGS.has(tag)) return `<${tag}${attrs}>`;
  return `<${tag}${attrs}>${children}</${tag}>`;
}

/**
 * Sanitize an HTML string, keeping a safe allowlist of tags.
 * Safe to render via dangerouslySetInnerHTML.
 */
export function sanitizeHtml(value: string | null | undefined): string {
  if (!value) return '';
  const doc = new DOMParser().parseFromString(String(value), 'text/html');
  return Array.from(doc.body.childNodes).map(_processNode).join('');
}

